/**
 * Network-event wiring for the enhanced game-core client.
 *
 * Subscribes to all `NetworkManager` events the renderer cares about and
 * either updates `gameState` directly (board deltas, player roster) or
 * dispatches a `gameupdate` CustomEvent for downstream listeners (UI,
 * gameLoop).
 *
 * `setupNetworkEvents` is idempotent — repeat calls are a no-op after
 * the first successful invocation.
 *
 * Local-only side effects (game-over pulse overlay, current-tetromino
 * re-render) are passed in by the caller so this module can stay
 * focused on the message dispatch.
 */

import gameState from '../utils/gameState.js';
import * as NetworkManager from '../utils/networkManager.js';
import { showToastMessage } from '../showToastMessage.js';
import {
	clearChessSelection, findChessPieceMeshAt, clearInFlightMove,
	removeChessMeshesAtCell, disposeChessPieceMesh,
} from '../chessInteraction.js';
import {
	ensureActivityLogUI,
	pushActivityEvent,
	loadActivityLogSnapshot,
	toggleActivityLog,
} from '../activityLog.js';
import {
	onCheckStart,
	onCheckClear,
	onCheckExpired,
	onCheckGameUpdate,
	initCheckAlert,
} from '../checkAlert.js';
import { updateGameStatusDisplay } from '../createLoadingIndicator.js';
import * as tetrominoModule from '../tetromino.js';
import { flashCellsBeforeClear, showChessCaptureAnimation } from '../tetromino/animations.js';
import {
	liftAirbornePieces,
	settleAirbornePieces,
} from '../wingAnimations.js';
import { cancelSkipChessTimer, cancelSkipDropTimer } from '../skipChessButton.js';
import { updateNextPieceHint } from '../tetromino/nextPiece.js';
import { getChessPiecesGroup, getCamera } from '../gameContext.js';
import { isEventInCurrentView } from '../battle/battleRules.js';
import { updateChessPieces } from '../updateChessPieces.js';
import { disposeBoats } from '../boatsRenderer.js';
import {
	showPromotionRedeemDialog,
	showFrozenPawnPromotionDialog,
	showKingBattleOverlay,
	showKingDuelOverlay,
	handleDuelRoundResult,
	handleDuelNewRound,
	showKingDuelResult,
} from '../uiOverlays.js';
import { playSound } from '../audio/soundManager.js';
import { mountMuteButton } from '../audio/muteButton.js';
import { applyServerPieces, syncDealtPieces } from '../tetromino/dealer.js';

// `king_detonation` and `island_decay` cap how many simultaneous
// particle animations we'll spawn so a 100×100 detonation doesn't pin
// the CPU.
const KING_ANIM_LIMITS = Object.freeze({
	maxAnimsPerLayer: 12,
	maxLayers: 20,
	maxTotalMs: 8000,
});

const ISLAND_DECAY_LIMITS = Object.freeze({
	maxAnims: 40,
	staggerMs: 50,
	maxTotalMs: 3000,
	// Don't restart a sand-dissolve on the same cell within this window.
	// Without it, repeated integrity ticks (every 10s) plus per-action
	// decay broadcasts can stack multiple animations on top of each other
	// for the same cell — that's most of the "lots of dissolving" the
	// user noticed.
	dedupeMs: 1500,
});

/**
 * Human-readable board coordinate for toasts. Battle arenas live at
 * absolute cells like (2002, 1995) — meaningless to a player, so
 * inside a battle we report positions relative to the arena centre.
 */
function formatCellForToast(x, z) {
	const centre = gameState.activeBattle?.centre;
	if (centre && Number.isFinite(centre.x) && Number.isFinite(centre.z)) {
		return `(${x - centre.x}, ${z - centre.z})`;
	}
	return `(${x}, ${z})`;
}

// `"x,z"` → last-playback-ms map used to suppress overlapping replays.
const recentIslandDecayPlaybacks = new Map();

let networkEventsInitialised = false;

function dispatchGameUpdate(detail) {
	try {
		if (!detail) return;
		window.dispatchEvent(new CustomEvent('gameupdate', { detail }));
	} catch (error) {
		console.warn('networkEvents: failed to dispatch gameupdate event:', error);
	}
}

let versionBannerShown = false;
function showVersionMismatchBanner({ serverVersion, clientVersion }) {
	if (versionBannerShown) return;
	versionBannerShown = true;
	console.warn(
		`[Version] Client bundle ${clientVersion} differs from server ${serverVersion} — prompting refresh.`
	);
	try {
		const existing = document.getElementById('version-mismatch-banner');
		if (existing) return;
		const banner = document.createElement('div');
		banner.id = 'version-mismatch-banner';
		banner.style.cssText = [
			'position:fixed', 'top:0', 'left:0', 'right:0',
			'z-index:99999',
			'padding:10px 14px',
			'background:#ffba00', 'color:#1c1c1c',
			'font-family:sans-serif', 'font-weight:600', 'font-size:14px',
			'box-shadow:0 2px 12px rgba(0,0,0,0.3)',
			'display:flex', 'align-items:center', 'justify-content:center',
			'gap:14px',
		].join(';');
		banner.innerHTML = `
			<span>A newer version of Tetches is available. Refresh to pick up the latest fixes.</span>
			<button id="version-mismatch-refresh" style="padding:6px 14px;border:0;border-radius:4px;cursor:pointer;background:#1c1c1c;color:#fff;font-weight:700;">Refresh now</button>
			<button id="version-mismatch-dismiss" style="padding:6px 10px;border:0;border-radius:4px;cursor:pointer;background:transparent;color:#1c1c1c;font-weight:600;">Later</button>
		`;
		document.body.appendChild(banner);
		document.getElementById('version-mismatch-refresh')?.addEventListener('click', () => {
			window.location.reload();
		});
		document.getElementById('version-mismatch-dismiss')?.addEventListener('click', () => {
			banner.remove();
		});
	} catch (err) {
		console.warn('Failed to render version-mismatch banner:', err);
	}
}

function normalisePlayersArrayToMap(playersArray) {
	const map = {};
	if (!Array.isArray(playersArray)) return map;
	for (const p of playersArray) {
		if (!p || !p.id) continue;
		// Carry every broadcast field through (the server's players list
		// is already payload-safe) and only coerce the ones consumers
		// rely on being well-typed. Cherry-picking here silently dropped
		// `paused`, `capturedBreakdown`, `promotionCreditCount` and the
		// battle-seat `color`/`battleId` the board painter needs.
		map[p.id] = {
			...p,
			name: p.name || p.id,
			isComputer: !!p.isComputer,
			eliminated: !!p.eliminated,
			capturedCount: Number(p.capturedCount) || 0,
			capturedSummary: p.capturedSummary && typeof p.capturedSummary === 'object'
				? { ...p.capturedSummary }
				: {},
		};
	}
	return map;
}

function applyBoardDelta(state) {
	if (!gameState.board) {
		gameState.board = { cells: {}, minX: 0, maxX: 0, minZ: 0, maxZ: 0 };
	}
	if (!gameState.board.cells) gameState.board.cells = {};

	for (const change of state.boardChanges) {
		if (!change) continue;
		const x = Number(change.x);
		const z = Number(change.z);
		if (!Number.isFinite(x) || !Number.isFinite(z)) continue;
		const key = `${x},${z}`;
		if (change.value === null || change.value === undefined) {
			delete gameState.board.cells[key];
		} else {
			gameState.board.cells[key] = change.value;
		}
	}

	if (Array.isArray(state.removedCells)) {
		for (const cell of state.removedCells) {
			if (!cell) continue;
			const x = Number(cell.x);
			const z = Number(cell.z);
			if (!Number.isFinite(x) || !Number.isFinite(z)) continue;
			delete gameState.board.cells[`${x},${z}`];
		}
	}

	if (state.boardBounds) {
		gameState.board.minX = state.boardBounds.minX;
		gameState.board.maxX = state.boardBounds.maxX;
		gameState.board.minZ = state.boardBounds.minZ;
		gameState.board.maxZ = state.boardBounds.maxZ;
		gameState.boardBounds = { ...state.boardBounds };
	}
}

function handleRowCleared(payload) {
	try {
		const rows = Array.isArray(payload?.rows) ? payload.rows : [];
		const cols = Array.isArray(payload?.cols) ? payload.cols : [];
		if (rows.length === 0 && cols.length === 0) return;

		const localId = gameState.localPlayerId;
		const isLocalActor = payload?.playerId && String(payload.playerId) === String(localId);

		// "Involves us" means either we triggered the clear OR one of
		// our chess pieces was sitting on a cleared cell (so it had to
		// fall / land somewhere). Anything else is purely background
		// — a bot or a stranger across the world clearing rows, which
		// must not make our local client beep on a loop.
		let touchesOurPieces = false;
		if (Array.isArray(payload?.settleOutcomes) && localId) {
			const pieces = Array.isArray(gameState.chessPieces) ? gameState.chessPieces : [];
			for (const outcome of payload.settleOutcomes) {
				if (!outcome) continue;
				let owner = outcome.pieceOwner || outcome.player || outcome.playerId;
				if (!owner && outcome.pieceId) {
					// Fallback for servers that don't include the owner
					// in the outcome (e.g. during a rolling deploy).
					const match = pieces.find(p => p && String(p.id) === String(outcome.pieceId));
					if (match) owner = match.player || match.playerId;
				}
				if (owner && String(owner) === String(localId)) {
					touchesOurPieces = true;
					break;
				}
			}
		}
		const involvesUs = isLocalActor || touchesOurPieces;

		// Sound rules:
		//   • Our own clears  → beep every cascade iteration (rewarding feedback).
		//   • Remote clear, but a piece of ours ends up airborne → one beep at
		//     the start of the cascade, never a chain of them.
		//   • Pure background clears (a stranger across the world) → silent.
		if (involvesUs) {
			const iter = Number(payload?.iteration);
			const allowSound = isLocalActor || !Number.isFinite(iter) || iter === 0;
			if (allowSound) {
				try { playSound('lineClear'); } catch (_e) { /* sound is best-effort */ }
			}
		}

		// Even when the clear was triggered by someone else, settle our
		// own airborne meshes — pieces on those cells need to land or
		// fall regardless of who finished the line.
		if (Array.isArray(payload?.settleOutcomes)) {
			settleAirbornePieces(payload.settleOutcomes);
		}

		if (!isLocalActor) return;
		const cellsCleared = Number(payload?.cellsCleared);
		const iter = Number(payload?.iteration);
		const suffix = Number.isFinite(iter) && iter > 0 ? ` (chain ×${iter + 1})` : '';
		if (Number.isFinite(cellsCleared) && cellsCleared === 0) {
			showToastMessage(`Pieces displaced — terrain unchanged${suffix}`);
			return;
		}
		const parts = [];
		if (rows.length) parts.push(`row${rows.length === 1 ? '' : 's'} ${rows.join(', ')}`);
		if (cols.length) parts.push(`col${cols.length === 1 ? '' : 's'} ${cols.join(', ')}`);
		showToastMessage(`Line cleared!${suffix} ${parts.join(' · ')}`);
	} catch (e) {
		console.error('Error handling row_cleared:', e);
	}
}

function handleCellsClearing(payload) {
	try {
		if (!payload || !Array.isArray(payload.cells) || payload.cells.length === 0) return;
		flashCellsBeforeClear(payload.cells, payload.durationMs, gameState);
		// Pieces that are about to be lifted off cleared squares grow
		// wings and hover until the row_cleared / game_update arrives
		// with the settle outcomes.
		if (Array.isArray(payload.airbornePieceIds) && payload.airbornePieceIds.length > 0) {
			liftAirbornePieces(payload.airbornePieceIds);
		}
	} catch (e) {
		console.error('Error handling cells_clearing:', e);
	}
}

function handleChessMoveBroadcast(payload) {
	try {
		const captured = payload?.capturedPiece;
		if (!captured) return;
		const localId = gameState.localPlayerId;
		const localMoved = localId && payload?.playerId && String(payload.playerId) === String(localId);
		const localLost = localId && captured.player && String(captured.player) === String(localId);
		if (!localMoved && !localLost) return;
		const capturedType = String(captured.type || 'piece').toLowerCase();
		// Toast with the *exact* server-authoritative capture cell so the
		// user can tell what really happened — previously the toast was a
		// generic "Your knight was captured!" with no location, leaving
		// the user staring at a random sand dissolve animation wondering
		// where their piece had actually died.
		const pos = captured.position
			|| payload.movedTo
			|| (Number.isFinite(payload.x) ? { x: payload.x, z: payload.z } : null);
		const atSuffix = pos && Number.isFinite(pos.x) && Number.isFinite(pos.z)
			? ` at ${formatCellForToast(pos.x, pos.z)}`
			: '';
		if (localLost) {
			showToastMessage(
				`Your ${capturedType}${atSuffix} was captured!`,
				{ variant: 'alert', duration: 5000 },
			);
		} else {
			showToastMessage(`Captured ${capturedType}${atSuffix}`, { variant: 'success' });
		}
	} catch (_) { /* toast is best-effort */ }
}

/**
 * Dedicated capture VFX driven by the server's new `chess_capture` event.
 *
 * Plays a flash + ring + particle burst at the server-authoritative cell
 * and (if the captured piece's mesh is still in the chess group) fades
 * the piece out in place. Crucially, we look up the mesh *by id* — if
 * the client has already removed the mesh (a stale game_update can yank
 * it before this event arrives), we still play the VFX at the correct
 * server cell instead of guessing from a now-gone client position.
 *
 * @param {Object} payload See `server/sockets/chess.js`.
 */
function handleChessCapture(payload) {
	try {
		if (!payload || !payload.at) return;
		const x = Number(payload.at.x);
		const z = Number(payload.at.z);
		if (!Number.isFinite(x) || !Number.isFinite(z)) return;

		try { playSound('capture'); } catch (_e) { /* sound is best-effort */ }

		let pieceMesh = null;
		const capturedId = payload?.capturedPiece?.id;
		if (capturedId) {
			const group = getChessPiecesGroup();
			if (group && Array.isArray(group.children)) {
				for (const child of group.children) {
					if (child?.userData?.id && String(child.userData.id) === String(capturedId)) {
						pieceMesh = child;
						break;
					}
				}
			}
		}
		if (!pieceMesh) {
			pieceMesh = findChessPieceMeshAt(x, z);
		}

		const keeperId = payload?.capturedBy?.pieceId || null;
		if (capturedId) {
			const group = getChessPiecesGroup();
			if (group && Array.isArray(group.children)) {
				for (const child of group.children) {
					if (child?.userData?.id && String(child.userData.id) === String(capturedId)) {
						disposeChessPieceMesh(child);
						break;
					}
				}
			}
		}
		removeChessMeshesAtCell(x, z, keeperId);

		showChessCaptureAnimation(x, z, gameState, { pieceMesh: null });
	} catch (e) {
		console.error('Error handling chess_capture:', e);
	}
}

/**
 * `chessFailed` recovery — server has told us the requested move could
 * not happen (piece already gone, source cell missing, etc.). The
 * previous client behaviour was to silently swallow the event, which is
 * how the user wound up with a knight that "vanished" from the screen
 * with no explanation. We now:
 *   1. Show a clear, position-free toast describing the reason.
 *   2. Force-clear any in-flight optimistic move (so the visual snap-back
 *      to the canonical server position happens immediately, not after
 *      the broken revert tween times out).
 *   3. Mark the chess piece group for an immediate re-sync, which will
 *      remove a stale piece mesh (if any) using the server-authoritative
 *      chess pieces list that arrived alongside this event.
 */
function handleChessFailed(payload) {
	try {
		const reason = payload && typeof payload.reason === 'string' ? payload.reason : null;
		const message = payload && payload.message ? String(payload.message) : 'Move could not be completed';

		// Rejected moves get an error cue so the player notices even
		// if they're not looking at the toast.
		try { playSound('error'); } catch (_e) { /* sound is best-effort */ }

		switch (reason) {
			case 'piece_gone':
				showToastMessage(
					'That piece is no longer on the board — the board has been refreshed.',
					{ variant: 'alert', duration: 4500 },
				);
				break;
			case 'desync_repaired':
				showToastMessage(message, { duration: 4500 });
				break;
			case 'rate_limited':
				// Rate limiting is already handled by the ack callback —
				// don't double-toast.
				break;
			default:
				showToastMessage(message, { variant: 'alert', duration: 4000 });
		}

		clearInFlightMove(gameState);
		gameState.processingMove = false;
		gameState._forceUpdate = true;
		clearChessSelection();
	} catch (e) {
		console.error('Error handling chessFailed:', e);
	}
}

function handlePawnDetonation(payload) {
	try {
		if (!payload) return;
		const localId = gameState.localPlayerId;
		const isLocal = localId && payload.playerId && String(payload.playerId) === String(localId);
		if (isLocal) return;
		const pieceType = String(payload.pieceType || 'PAWN').toUpperCase();
		showToastMessage(pieceType === 'KING'
			? 'Opponent detonated their king!'
			: 'Opponent detonated a pawn!');
	} catch (_) { /* toast is best-effort */ }
}

function handleKingDetonation(payload, { showGameOverPulseOverlay }) {
	try {
		if (!payload || !Array.isArray(payload.explosionSequence)) return;

		const layerIntervalMs = Number(payload.layerIntervalMs) > 0
			? Number(payload.layerIntervalMs)
			: 500;

		const sequence = payload.explosionSequence
			.filter(cell => cell && Number.isFinite(cell.x) && Number.isFinite(cell.z))
			.map(cell => {
				const hasDistance = Number.isFinite(cell.distance);
				const fallbackDistance =
					Number.isFinite(payload?.detonatedAt?.x) && Number.isFinite(payload?.detonatedAt?.z)
						? Math.abs(cell.x - payload.detonatedAt.x) + Math.abs(cell.z - payload.detonatedAt.z)
						: 0;
				return {
					x: cell.x,
					z: cell.z,
					distance: hasDistance ? Number(cell.distance) : fallbackDistance,
				};
			});

		const localId = gameState.localPlayerId;
		const isLocalDetonation = localId && payload.playerId && String(payload.playerId) === String(localId);

		// Always announce — every player on the board should see the king
		// going up in a fireball, not just the unlucky one whose king it is.
		try {
			if (typeof showToastMessage === 'function') {
				const reason = String(payload?.reason || '').toLowerCase();
				const isAi = reason.startsWith('ai_');
				if (isLocalDetonation) {
					showToastMessage('Your king has detonated!', { variant: 'alert', duration: 6000 });
				} else if (isAi) {
					showToastMessage('An AI was reduced to a lone king — Bye Bye!', { duration: 4500 });
				} else {
					showToastMessage('An opponent detonated their king!', { duration: 4500 });
				}
			}
		} catch (_) { /* toast is best-effort */ }

		if (typeof window.showExplosionAnimation !== 'function') return;

		const layerMap = new Map();
		for (const cell of sequence) {
			if (!layerMap.has(cell.distance)) layerMap.set(cell.distance, []);
			layerMap.get(cell.distance).push(cell);
		}

		const sortedDistances = [...layerMap.keys()]
			.sort((a, b) => b - a)
			.slice(0, KING_ANIM_LIMITS.maxLayers);

		const effectiveInterval = Math.min(
			layerIntervalMs,
			Math.floor(KING_ANIM_LIMITS.maxTotalMs / Math.max(sortedDistances.length, 1))
		);

		sortedDistances.forEach((distance, layerIdx) => {
			let layerCells = layerMap.get(distance);
			if (layerCells.length > KING_ANIM_LIMITS.maxAnimsPerLayer) {
				const step = Math.ceil(layerCells.length / KING_ANIM_LIMITS.maxAnimsPerLayer);
				layerCells = layerCells.filter((_, i) => i % step === 0);
			}
			setTimeout(() => {
				for (const cell of layerCells) {
					try { window.showExplosionAnimation(cell.x, cell.z, gameState); } catch (_) { /* ignore */ }
				}
			}, layerIdx * effectiveInterval);
		});

		if (isLocalDetonation && typeof showGameOverPulseOverlay === 'function') {
			const totalDuration = sortedDistances.length * effectiveInterval;
			setTimeout(() => showGameOverPulseOverlay('GAME OVER'), totalDuration + 180);
		}
	} catch (e) {
		console.error('Error handling king_detonation:', e);
	}
}

// `king_detonation_layer` events are emitted server-side once per layer of
// cell removal so clients that joined mid-detonation can still react. The
// main `king_detonation` event already pre-schedules every layer's
// animation locally, so for already-connected clients we deliberately
// **don't** spawn additional particles here (it would double-up). We just
// observe the event so future extensions (sounds, accessibility cues) have
// a hook.
function handleKingDetonationLayer(_payload) {
	// no-op for now — see comment above.
}

function handleIslandAtRisk(payload) {
	try {
		if (!payload || !payload.playerId) return;
		const localId = gameState.localPlayerId;
		if (!localId || String(payload.playerId) !== String(localId)) return;

		const remainingMs = Number(payload.remainingMs) || 0;
		const remainingMoves = Number.isFinite(payload.remainingMoves) ? payload.remainingMoves : null;
		const hasPiece = !!payload.hasPiece;
		const cellCount = Array.isArray(payload.cells) ? payload.cells.length : 0;
		const thresholdType = payload.thresholdType === 'moves' ? 'moves' : 'ms';

		const subject = hasPiece
			? (cellCount > 1 ? `${cellCount} cells & a piece` : 'A piece')
			: (cellCount > 1 ? `${cellCount} cells` : 'A cell');

		let urgency;
		let message;
		if (thresholdType === 'moves' && remainingMoves !== null) {
			urgency = remainingMoves <= 1 ? 'alert' : undefined;
			const moveWord = remainingMoves === 1 ? 'move' : 'moves';
			message = `${subject} of yours will decay in ${remainingMoves} ${moveWord} — bridge back to your king!`;
		} else {
			urgency = remainingMs <= 15000 ? 'alert' : undefined;
			const secs = Math.max(0, Math.round(remainingMs / 1000));
			message = `${subject} of yours will decay in ~${secs}s — bridge back to your king!`;
		}

		showToastMessage(
			message,
			urgency ? { variant: urgency, duration: 6000 } : { duration: 5000 }
		);
	} catch (e) {
		console.error('Error handling island_at_risk:', e);
	}
}

function handleIslandDecay(payload) {
	try {
		const cells = payload?.cells;
		if (!Array.isArray(cells) || cells.length === 0) return;

		const playSand = (typeof window.showSandDissolveCellAnimation === 'function')
			? window.showSandDissolveCellAnimation
			: null;
		const playExplosion = (typeof window.showExplosionAnimation === 'function')
			? window.showExplosionAnimation
			: null;
		if (!playSand && !playExplosion) return;

		const now = Date.now();
		// Sweep stale dedupe entries so the map doesn't grow forever.
		for (const [key, ts] of recentIslandDecayPlaybacks) {
			if (now - ts > ISLAND_DECAY_LIMITS.dedupeMs * 2) {
				recentIslandDecayPlaybacks.delete(key);
			}
		}

		const validCells = cells.filter(cell => {
			if (!cell || !Number.isFinite(cell.x) || !Number.isFinite(cell.z)) return false;
			const key = `${cell.x},${cell.z}`;
			const last = recentIslandDecayPlaybacks.get(key);
			if (last && now - last < ISLAND_DECAY_LIMITS.dedupeMs) return false;
			return true;
		});
		if (validCells.length === 0) return;

		const capped = validCells.length > ISLAND_DECAY_LIMITS.maxAnims
			? validCells.filter((_, i) => i % Math.ceil(validCells.length / ISLAND_DECAY_LIMITS.maxAnims) === 0)
			: validCells;

		const stagger = Math.min(
			ISLAND_DECAY_LIMITS.staggerMs,
			Math.floor(ISLAND_DECAY_LIMITS.maxTotalMs / Math.max(capped.length, 1))
		);

		capped.forEach((cell, idx) => {
			recentIslandDecayPlaybacks.set(`${cell.x},${cell.z}`, now + idx * stagger);
			setTimeout(() => {
				try {
					if (playSand) playSand(cell.x, cell.z, gameState);
					else if (playExplosion) playExplosion(cell.x, cell.z, gameState);
				} catch (_) { /* ignore animation errors */ }
			}, idx * stagger);
		});
	} catch (e) {
		console.error('Error handling island_decay:', e);
	}
}

function handleNoValidChessMoves(payload) {
	try {
		if (!payload?.playerId || String(payload.playerId) !== String(gameState.localPlayerId)) return;
		clearChessSelection();
		cancelSkipChessTimer();
		cancelSkipDropTimer();
		gameState.processingMove = false;
		showToastMessage('No chess moves available — dropping next piece');
		gameState.turnPhase = 'tetris';
		updateGameStatusDisplay(gameState);
		updateNextPieceHint(gameState);
	} catch (e) {
		console.error('Error handling no_valid_chess_moves:', e);
	}
}

function handleNewTetromino(payload, { renderCurrentTetromino }) {
	try {
		const tetromino = payload?.tetromino;
		if (!tetromino) return;
		clearChessSelection();
		cancelSkipChessTimer();
		cancelSkipDropTimer();
		gameState.processingMove = false;
		// The server dealt this piece; spawn exactly that and adopt its deal.
		const tetrominoType = payload.pieces?.current || tetromino.type || tetromino.pieceType;
		const newTetromino = tetrominoModule.initializeNewTetromino(gameState, tetrominoType);
		gameState.currentTetromino = newTetromino || tetrominoModule.initializeNextTetromino(gameState);
		if (newTetromino) applyServerPieces(gameState, payload.pieces, { used: true });
		gameState.turnPhase = 'tetris';
		if (typeof renderCurrentTetromino === 'function') renderCurrentTetromino();
		updateGameStatusDisplay(gameState);
		updateNextPieceHint(gameState);
	} catch (e) {
		console.error('Error handling new_tetromino:', e);
	}
}

function handlePawnPromotionAvailable(payload) {
	// Legacy handler — the server no longer fires this event for new
	// games (the chess_move handler auto-banks a credit instead and
	// emits `promotion_credit_added`). Kept as a no-op so older
	// rolling servers behind the live client don't spam console
	// errors.
	void payload;
}

function handleKingCaptured(payload) {
	try {
		const { captorId, captorName, defeatedId, defeatedName, defeatedColor, inheritedPawnCount } = payload || {};
		if (!captorId || !defeatedId) return;
		const localId = gameState.localPlayerId;
		const involvesUs = localId && (
			String(captorId) === String(localId)
			|| String(defeatedId) === String(localId)
		);
		if (!involvesUs) return;
		try { playSound('kingFall'); } catch (_e) { /* sound is best-effort */ }

		const weWereDefeated = String(defeatedId) === String(localId);
		if (weWereDefeated) {
			showToastMessage(
				`Your king was captured. Your army now fights for ${captorName || captorId} ` +
				`(pieces turn their colour) until suicidal pawns detonate.`,
				{ variant: 'alert', duration: 9000 }
			);
		} else {
			showToastMessage(
				`You captured ${defeatedName || defeatedId}'s king — their forces are now yours.`,
				{ variant: 'success', duration: 6000 }
			);
		}

		showKingBattleOverlay(captorId, captorName, defeatedId, defeatedName, defeatedColor, inheritedPawnCount);

		const group = getChessPiecesGroup();
		const camera = getCamera();
		if (group && camera) {
			updateChessPieces(group, camera, { ...gameState, _forceUpdate: true });
		}
	} catch (e) {
		console.error('Error handling king_captured:', e);
	}
}

function handleKingRespawned(payload) {
	try {
		if (!payload || !payload.playerId) return;
		const isLocal = String(payload.playerId) === String(gameState.localPlayerId);
		const remaining = Number(payload.remainingLives);
		const total = Number(payload.totalLives) || 3;
		const safeRemaining = Number.isFinite(remaining) ? remaining : (total - 1);
		const reason = String(payload.reason || 'unknown').replace(/_/g, ' ');
		const livesWord = safeRemaining === 1 ? 'life' : 'lives';

		if (isLocal) {
			showToastMessage(
				`Your king fell (${reason})! ${safeRemaining} ${livesWord} left.`,
				{ variant: 'alert', duration: 6500 }
			);
			try { playSound('kingFall'); } catch (_e) { /* sound is best-effort */ }
		}
		// Remote king falls aren't toasted or sounded — they're noisy
		// in a busy world and the player can read about them in the
		// activity log if they care.

		// Sync local state so the player bar + sidebar reflect the
		// remaining lives without waiting for the next full game_update.
		if (gameState.players && gameState.players[payload.playerId]) {
			gameState.players[payload.playerId].kingLives = safeRemaining;
		}

		if (typeof window.updateBoardVisuals === 'function') {
			try { window.updateBoardVisuals(); } catch (_e) { /* best-effort */ }
		}
	} catch (e) {
		console.error('Error handling king_respawned:', e);
	}
}

function handleKingEliminated(payload) {
	try {
		if (!payload || !payload.playerId) return;
		const isLocal = String(payload.playerId) === String(gameState.localPlayerId);
		if (gameState.players && gameState.players[payload.playerId]) {
			gameState.players[payload.playerId].eliminated = true;
			gameState.players[payload.playerId].kingLives = 0;
		}
		if (isLocal) {
			// The on-screen drama (explosions + "GAME OVER" pulse) is owned
			// by the king_detonation event that always accompanies a final
			// death now, so we don't double-toast here — just the death
			// sound, which pairs with the detonation animation.
			try { playSound('kingFall'); } catch (_e) { /* sound is best-effort */ }
		}
		// Remote eliminations are intentionally silent — the activity
		// log still records them so spectators can watch the scoreboard.
	} catch (e) {
		console.error('Error handling king_eliminated:', e);
	}
}

function handleSuicidalPawn(payload) {
	try {
		const { x, z, remaining } = payload || {};
		if (x === undefined || z === undefined) return;
		if (typeof window.showExplosionAnimation === 'function') {
			window.showExplosionAnimation(x, z, gameState);
		}
		if (remaining === 0) {
			showToastMessage('All inherited pawns have self-destructed!');
		}
	} catch (e) {
		console.error('Error handling suicidal_pawn:', e);
	}
}

function handleKingDuelStart(payload) {
	try {
		const { duelId, gridCols, gridRows, opponentName } = payload || {};
		if (!duelId) return;
		showKingDuelOverlay(duelId, gridCols || 4, gridRows || 2, opponentName || 'Opponent');
	} catch (e) {
		console.error('Error handling king_duel_start:', e);
	}
}

function handleKingDuelAnnounced(payload) {
	try {
		const { player1, player2, player1Name, player2Name } = payload || {};
		// A duel in someone else's battle (or in the world while we're
		// battling) is not our news — view isolation covers toasts too.
		if (!isEventInCurrentView(gameState, [player1, player2])) return;
		showToastMessage(
			`King's Duel! ${player1Name} vs ${player2Name} — both captured each other's king!`,
			5000,
		);
	} catch (e) {
		console.error('Error handling king_duel_announced:', e);
	}
}

/**
 * Wire up every NetworkManager event the renderer cares about.
 *
 * @param {Object} hooks
 * @param {Function} [hooks.showGameOverPulseOverlay]
 * @param {Function} [hooks.renderCurrentTetromino]
 */
export function setupNetworkEvents(hooks = {}) {
	if (networkEventsInitialised) return;
	networkEventsInitialised = true;
	try { disposeBoats(); } catch (_e) { /* fleet retired */ }

	if (!NetworkManager || typeof NetworkManager.on !== 'function') {
		console.warn('setupNetworkEvents: NetworkManager not available');
		return;
	}
	try { initCheckAlert(); }
	catch (e) { console.warn('initCheckAlert failed:', e); }

	NetworkManager.on('game_state', (payload) => {
		const state = payload?.state || payload;
		if (!state || typeof state !== 'object') return;
		if (payload && Array.isArray(payload.players)) {
			state.players = normalisePlayersArrayToMap(payload.players);
		}
		dispatchGameUpdate(state);
	});

	NetworkManager.on('game_update', (payload) => {
		const state = payload?.state || payload;
		if (!state || typeof state !== 'object') return;

		if (Array.isArray(state.players)) {
			state.players = normalisePlayersArrayToMap(state.players);
		}

		if (state.fullUpdate === false && Array.isArray(state.boardChanges)) {
			applyBoardDelta(state);
			dispatchGameUpdate({ ...state, board: gameState.board });
			if (Array.isArray(state.chessPieces)) {
				const group = getChessPiecesGroup();
				const camera = getCamera();
				if (group && camera) {
					updateChessPieces(group, camera, { ...gameState, _forceUpdate: true });
				}
			}
			return;
		}
		dispatchGameUpdate(state);
	});

	NetworkManager.on('player_joined', (payload) => {
		if (!payload || !Array.isArray(payload.players)) return;
		dispatchGameUpdate({ players: normalisePlayersArrayToMap(payload.players) });
	});

	NetworkManager.on('player_left', (payload) => {
		if (!payload || !Array.isArray(payload.players)) return;
		dispatchGameUpdate({ players: normalisePlayersArrayToMap(payload.players) });
	});

	NetworkManager.on('player_id', (payload) => {
		if (!payload?.playerId) return;
		dispatchGameUpdate({ localPlayerId: payload.playerId });
	});

	// Server tells us which client build it expects. If our bundle is
	// older we render a non-blocking "please refresh" banner so the
	// player picks up bug fixes / rule changes. A cheating front-end
	// that spoofs an old bundle version still works, but at least it
	// can't claim "I didn't know" — and all of its game-state changes
	// are server-validated anyway.
	NetworkManager.on('server_version', (payload) => {
		try {
			const serverVersion = String(payload?.bundleVersion || '');
			const clientVersion = String(window.__BUNDLE_VERSION__ || '');
			if (!serverVersion || !clientVersion) return;
			if (serverVersion === clientVersion) return;
			showVersionMismatchBanner({ serverVersion, clientVersion });
		} catch (err) {
			console.warn('server_version handler failed:', err);
		}
	});

	const safe = (label, fn) => (payload) => {
		try { fn(payload); } catch (e) { console.error(`Error handling ${label}:`, e); }
	};

	NetworkManager.on('row_cleared', handleRowCleared);
	NetworkManager.on('cells_clearing', handleCellsClearing);
	NetworkManager.on('chess_move', handleChessMoveBroadcast);
	NetworkManager.on('chess_capture', handleChessCapture);
	NetworkManager.on('chessFailed', handleChessFailed);
	NetworkManager.on('pawn_detonation', handlePawnDetonation);
	NetworkManager.on('king_detonation', (payload) => handleKingDetonation(payload, hooks));
	NetworkManager.on('king_detonation_layer', handleKingDetonationLayer);
	NetworkManager.on('island_decay', handleIslandDecay);
	NetworkManager.on('island_at_risk', handleIslandAtRisk);
	NetworkManager.on('no_valid_chess_moves', handleNoValidChessMoves);
	NetworkManager.on('new_tetromino', (payload) => handleNewTetromino(payload, hooks));
	NetworkManager.on('pawn_promotion_available', handlePawnPromotionAvailable);
	NetworkManager.on('king_captured', handleKingCaptured);
	NetworkManager.on('king_respawned', handleKingRespawned);
	NetworkManager.on('king_eliminated', handleKingEliminated);
	// Check (deferred king-capture). The server holds the would-be
	// capture for `CHECK_DEADLINE_MS` so the defender has a chance to
	// escape. `chess_check` is the warning, `chess_check_cleared` is
	// the defender's successful escape, and `chess_check_expired` is
	// the timer running out (the king capture follows immediately).
	NetworkManager.on('chess_check', safe('chess_check', (payload) => {
		try { onCheckStart(payload); }
		catch (e) { console.warn('checkAlert start failed:', e); }
	}));
	NetworkManager.on('chess_check_cleared', safe('chess_check_cleared', (payload) => {
		try { onCheckClear(payload); }
		catch (e) { console.warn('checkAlert clear failed:', e); }
	}));
	NetworkManager.on('chess_check_expired', safe('chess_check_expired', (payload) => {
		try { onCheckExpired(payload); }
		catch (e) { console.warn('checkAlert expired failed:', e); }
	}));
	NetworkManager.on('game_update', safe('game_update', (payload) => {
		// `pendingCheck` rides on every `game_update` payload (set or
		// null). Treat it as the source of truth so late-joiners and
		// reconnects also surface any active warning.
		try { onCheckGameUpdate(payload); }
		catch (e) { console.warn('checkAlert game_update reconcile failed:', e); }
	}));
	NetworkManager.on('suicidal_pawn', handleSuicidalPawn);
	NetworkManager.on('king_duel_start', handleKingDuelStart);
	NetworkManager.on('king_duel_round_result', safe('king_duel_round_result', handleDuelRoundResult));
	NetworkManager.on('king_duel_new_round', safe('king_duel_new_round', handleDuelNewRound));
	NetworkManager.on('king_duel_result', safe('king_duel_result', showKingDuelResult));
	NetworkManager.on('king_duel_announced', handleKingDuelAnnounced);
	// Server-initiated rename broadcast. The local UI already
	// updated localStorage when the user submitted the dialog —
	// this handler covers everyone *else* seeing the new name.
	NetworkManager.on('player_renamed', (payload) => {
		try {
			if (!payload || !payload.playerId) return;
			const players = gameState.players || {};
			if (players[payload.playerId]) {
				players[payload.playerId].name = payload.playerName;
			}
			if (payload.playerId === gameState.localPlayerId && payload.playerName) {
				try { localStorage.setItem('playerName', payload.playerName); } catch (_e) { /* ignore */ }
			}
			if (Array.isArray(payload.players)) {
				dispatchGameUpdate({ players: payload.players });
			} else {
				dispatchGameUpdate({});
			}
		} catch (e) {
			console.warn('[player_renamed] update failed:', e);
		}
	});

	NetworkManager.on('activity_event', (payload) => {
		// Audio hooks for activity events that don't have a dedicated
		// handler. We ONLY fire these for the local player — a busy
		// shared world (especially with AI bots placing 1-2 pieces
		// per second) would otherwise produce a constant background
		// of clatter the user can do nothing about.
		try {
			const type = payload && payload.type ? String(payload.type) : '';
			const isLocal = payload && payload.playerId
				&& String(payload.playerId) === String(gameState.localPlayerId);
			if (isLocal) {
				switch (type) {
					case 'tetromino_placed':
						playSound('drop');
						break;
					case 'pawn_promoted_to_credit':
						playSound('promotion');
						break;
					default:
						break;
				}
			}
		} catch (_e) { /* sound is best-effort */ }
		pushActivityEvent(payload);
	});
	NetworkManager.on('activity_log_snapshot', (payload) => loadActivityLogSnapshot(payload));

	// Private per-player captured-piece basket. The server emits this
	// on join, capture, and promotion-from-basket. Stored on the
	// game state so the promotion dialog can offer the right options.
	NetworkManager.on('captured_basket', (payload) => {
		const basket = Array.isArray(payload?.basket) ? payload.basket : [];
		dispatchGameUpdate({ capturedBasket: basket });
	});

	// Power-up orbs. Each event is small — single orb id + position
	// + piece type. We mutate `gameState.powerUps` in place and let
	// the renderer pick it up on the next frame. The full set is
	// also delivered with every `game_update`, so this just gives us
	// snappier reaction times for "first to connect wins" moments.
	NetworkManager.on('powerup_spawned', (payload) => {
		if (!payload || !payload.id) return;
		const list = Array.isArray(gameState.powerUps) ? gameState.powerUps : [];
		const filtered = list.filter(o => o && o.id !== payload.id);
		filtered.push({
			id: payload.id,
			x: payload.x,
			z: payload.z,
			pieceType: payload.pieceType,
			spawnedAt: payload.spawnedAt,
			expiresAt: payload.expiresAt,
			targetPlayerId: payload.targetPlayerId || null,
		});
		gameState.powerUps = filtered;
		dispatchGameUpdate({ powerUps: filtered.slice() });
	});

	NetworkManager.on('powerup_claimed', (payload) => {
		if (!payload || !payload.orbId) return;
		const list = Array.isArray(gameState.powerUps) ? gameState.powerUps : [];
		gameState.powerUps = list.filter(o => o && o.id !== payload.orbId);
		dispatchGameUpdate({ powerUps: gameState.powerUps.slice() });

		try {
			const isLocal = payload.playerId && payload.playerId === gameState.localPlayerId;
			const label = String(payload.pieceType || 'piece').toLowerCase();
			// Toast + sound for our own claim. For remote claims, log
			// the activity (the snapshot listener already does that)
			// but stay quiet — no toast, no audio. A live world has
			// many players, and an orb claim chime every few seconds
			// for actions we can't influence is just noise.
			if (isLocal) {
				showToastMessage(`Power-up claimed: ${label}!`, 3000);
				try { playSound('orbClaim'); } catch (_e) { /* sound is best-effort */ }
			}
		} catch (toastErr) {
			console.warn('[powerup_claimed] toast failed:', toastErr);
		}
	});

	NetworkManager.on('powerup_expired', (payload) => {
		if (!payload || !payload.orbId) return;
		const list = Array.isArray(gameState.powerUps) ? gameState.powerUps : [];
		gameState.powerUps = list.filter(o => o && o.id !== payload.orbId);
		dispatchGameUpdate({ powerUps: gameState.powerUps.slice() });
	});

	// Promotion credits — the local player only. The server pushes the
	// full list on join (and after every credit lifecycle event); we
	// also handle the individual `_added` / `_redeemed` events so we
	// can show a toast immediately without waiting for the full list.
	NetworkManager.on('promotion_credits', (payload) => {
		const credits = Array.isArray(payload?.credits) ? payload.credits : [];
		gameState.promotionCredits = credits.slice();
		dispatchGameUpdate({ promotionCredits: credits.slice() });
	});

	NetworkManager.on('promotion_credit_added', (payload) => {
		if (!payload || !payload.creditId) return;
		const isLocal = payload.playerId && payload.playerId === gameState.localPlayerId;
		// Sound only for our own promotions — remote promotions are
		// activity-log only so the audio doesn't ping every few seconds
		// in a busy world.
		if (isLocal) {
			try { playSound('promotion'); } catch (_e) { /* sound is best-effort */ }
		}
		if (isLocal) {
			const list = Array.isArray(gameState.promotionCredits) ? gameState.promotionCredits : [];
			if (!list.some(c => c && c.id === payload.creditId)) {
				list.push({
					id: payload.creditId,
					originalX: payload.originalX,
					originalZ: payload.originalZ,
					createdAt: payload.createdAt,
				});
				gameState.promotionCredits = list.slice();
				dispatchGameUpdate({ promotionCredits: gameState.promotionCredits.slice() });
			}
			// If they already have captured pieces, the redeem dialog
			// is immediately useful — pop it open. Otherwise just toast
			// so they know a credit's been banked.
			const basket = Array.isArray(gameState.capturedBasket) ? gameState.capturedBasket : [];
			const hasRedeemableCapture = basket.some(item => {
				const t = String(item?.type || '').toUpperCase();
				return ['QUEEN', 'ROOK', 'BISHOP', 'KNIGHT'].includes(t);
			});
			try {
				if (hasRedeemableCapture) {
					showPromotionRedeemDialog();
				} else {
					showToastMessage('Pawn promoted! Capture a piece to redeem this credit.', 3500);
				}
			} catch (uiErr) {
				console.warn('[promotion_credit_added] UI failed:', uiErr);
			}
		}
	});

	// Frozen-pawn promotion: server tells us a pawn has reached the
	// promotion line and is now locked in place. Mark it on local
	// state (so the renderer can draw the glow halo immediately) and
	// pop the deployment dialog for the local player.
	NetworkManager.on('pawn_awaiting_promotion', (payload) => {
		if (!payload || !payload.pieceId) return;
		const isLocal = payload.playerId && payload.playerId === gameState.localPlayerId;
		const list = Array.isArray(gameState.chessPieces) ? gameState.chessPieces : null;
		if (list) {
			const piece = list.find(p => p && String(p.id) === String(payload.pieceId));
			if (piece) {
				piece.awaitingPromotion = true;
				piece.awaitingPromotionAt = payload.awaitingSince || Date.now();
			}
		}
		if (isLocal) {
			try { playSound('promotion'); } catch (_e) { /* sound is best-effort */ }
			if (payload.firstTime !== false) {
				// `forceShow` lets the dialog open even when the local
				// chessPieces array hasn't received the awaitingPromotion
				// flag yet (the trailing game_update can be a beat behind
				// this event). Otherwise the "nothing happens" symptom
				// the user reported would re-occur on a slow snapshot.
				try { showFrozenPawnPromotionDialog(payload.pieceId, { forceShow: true }); }
				catch (uiErr) { console.warn('[pawn_awaiting_promotion] UI failed:', uiErr); }
			}
		}
	});

	// Pawn promotion deployed: server replaced the frozen pawn with a
	// captured piece. Drop the local frozen marker preemptively so the
	// halo disappears without waiting for the broadcast; surface a
	// confirmation toast for the local player.
	NetworkManager.on('pawn_promotion_deployed', (payload) => {
		if (!payload || !payload.pawnId) return;
		const pieces = Array.isArray(gameState.chessPieces) ? gameState.chessPieces : null;
		if (pieces) {
			const stale = pieces.find(p => p && String(p.id) === String(payload.pawnId));
			if (stale) {
				stale.awaitingPromotion = false;
				delete stale.awaitingPromotionAt;
			}
		}
		const isLocal = payload.playerId && payload.playerId === gameState.localPlayerId;
		if (!isLocal) return;
		try {
			showToastMessage(
				`${payload.pieceType || 'Piece'} deployed at ${formatCellForToast(payload.x, payload.z)}`,
				3500,
			);
		} catch (toastErr) {
			console.warn('[pawn_promotion_deployed] toast failed:', toastErr);
		}
	});

	NetworkManager.on('promotion_credit_redeemed', (payload) => {
		if (!payload || !payload.creditId) return;
		const isLocal = payload.playerId && payload.playerId === gameState.localPlayerId;
		if (isLocal) {
			const list = Array.isArray(gameState.promotionCredits) ? gameState.promotionCredits : [];
			gameState.promotionCredits = list.filter(c => c && c.id !== payload.creditId);
			dispatchGameUpdate({ promotionCredits: gameState.promotionCredits.slice() });
			try {
				const where = payload.fallback
					? `near your king (original cell gone)`
					: `at ${formatCellForToast(payload.x, payload.z)}`;
				showToastMessage(`${payload.pieceType || 'piece'} deployed ${where}`, 3500);
			} catch (toastErr) {
				console.warn('[promotion_credit_redeemed] toast failed:', toastErr);
			}
		}
	});

	try {
		ensureActivityLogUI();
		mountMuteButton();
		window.gameCore = window.gameCore || {};
		window.gameCore.toggleActivityLog = toggleActivityLog;
	} catch (error) {
		console.warn('[NetworkEvents] Failed to set up activity log UI:', error);
	}

	let hadConnection = false;

	NetworkManager.on('disconnect', () => {
		showToastMessage('Connection lost — reconnecting…', 4000);
	});

	NetworkManager.on('connect', () => {
		if (!hadConnection) {
			hadConnection = true;
		} else {
			showToastMessage('Reconnected!', 2000);
			if (gameState.localPlayerId && typeof NetworkManager.joinGame === 'function') {
				NetworkManager.joinGame()
					.then(() => syncDealtPieces(gameState))
					.catch(() => {});
			}
		}
		// Fetch activity log snapshot every (re)connect so a returning
		// player isn't staring at a stale empty panel.
		try {
			NetworkManager.sendMessage('get_activity_log', null);
		} catch (error) {
			// not connected yet; the snapshot will arrive via the
			// `activity_log_snapshot` broadcast on initial join.
		}
	});
}
